import type { Batch } from '@ctcells/contract'
import { MODULE_KEYS, type ModuleKey, safeStorage } from '@ctcells/core'

/**
 * 작업 줄이 **들은 모듈별 스텝 수를 브라우저에 기억한다** — eric 2026-09-28
 * 「전체 스텝 중에... 현재 스텝을 맨 오른쪽 숫자에」 · 「전체 스텝에서.. 각각의 모듈별 스텝의 양으로..
 * 프로그레스바 길이도 조절하라고 했잖아」.
 *
 * 🔴 **왜 기억하나.** 모듈 총수의 출처는 구독 프레임뿐이다 — `Batch` 응답 · `/history` · `event_log`
 *    어디에도 없다(팀장 실측 2026-09-28). 그래서 새로 고치면 끝난 모듈의 수를 잃고, 시작 전 모듈의
 *    총수는 끝까지 모른다. 백엔드 요청 대신 프론트가 들은 것을 적어 둔다(eric 방침).
 *
 * 두 갈래
 *
 * | 갈래 | 키 | 값 | 쓰임 |
 * | --- | --- | --- | --- |
 * | (a) 배치 | `batch_id` × 모듈 | 마지막 `{step, total}` | 새로 고친 뒤 **그 배치의** 끝난 모듈 복원 |
 * | (b) 레시피 | `recipe_id@version#sample_count` × 모듈 | `total` | 아직 시작 안 한 모듈의 **예상** 총수 |
 *
 * 🔴 **(b) 의 근거**(2026-09-28 코드 대조): 모듈의 `total` 은 `len(block)` 이고(module-core
 *    `app/core/runtime.py` `Runtime.start`), 블록은 L2 가 `compile_block(recipe, phase=…)` 로 짓는다
 *    (ctcells-core `app/services/batch_dispatch.py`). **입력은 레시피 본문뿐이고 시료는 들어가지 않는다.**
 *    ⇒ 같은 레시피·버전이면 같은 수다. 시료 수까지 키에 넣은 것은 **보수적으로** 좁힌 것이다.
 *    ⚠ 같은 레시피라도 서버 설정(`RECIPE_INSERT_MOVE_TO` 등)·전개 규칙 문서가 바뀌면 수가 달라진다 —
 *      그래서 (b) 는 «예상»이고 화면이 그렇게 표시하며, **실제 프레임이 오면 늘 실제 값이 이긴다.**
 *
 * 🔴 **저장소는 `safeStorage` 하나를 지난다**(플랫폼 계층 · 전부 try/catch). 막힌 환경(사생활 모드 ·
 *    용량 초과 · 깨진 JSON)에서는 «기억이 없는 것»으로 서고 화면은 그대로 그려진다.
 * ⚠ 서버 상태가 아니다 — 들은 수의 사본이다. 버튼 활성 · 상태 판정에 쓰지 않는다.
 */

const STORAGE_KEY = 'ctcells.strip.moduleSteps.v1'

/** 오래 켜 둔 단말에서 무한히 늘지 않게. 넘치면 오래된 것부터 버린다 */
const MAX_BATCHES = 20
const MAX_RECIPES = 30

export interface StepCount {
  step?: number | undefined
  total?: number | undefined
}

export type ModuleStepCounts = ReadonlyMap<ModuleKey, StepCount>

interface Stored {
  batches: Record<string, { at: number; modules: Partial<Record<ModuleKey, StepCount>> }>
  recipes: Record<string, { at: number; totals: Partial<Record<ModuleKey, number>> }>
}

/** 기억에서 읽은 것 — 그 배치의 것(a)과 그 레시피의 예상(b) */
export interface StepMemory {
  batch: ModuleStepCounts
  estimate: ReadonlyMap<ModuleKey, number>
}

export const EMPTY_STEP_MEMORY: StepMemory = { batch: new Map(), estimate: new Map() }

/**
 * (b) 의 키. **버전을 모르면 없다** — 다른 버전의 수를 가져다 쓰지 않는다.
 */
export function recipeMemoryKey(
  batch: Pick<Batch, 'recipe_id' | 'recipe_version' | 'sample_count'>,
): string | undefined {
  const version = batch.recipe_version as number | null | undefined
  const samples = batch.sample_count as number | null | undefined
  if (typeof version !== 'number' || typeof samples !== 'number') return undefined
  return `${batch.recipe_id}@${String(version)}#${String(samples)}`
}

const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0

/** 깨진 값은 버린다 — 저장소가 오염돼도 화면은 선다 */
function load(): Stored {
  const raw = safeStorage.readJson<Partial<Stored>>(STORAGE_KEY)
  const batches = raw !== null && typeof raw.batches === 'object' ? raw.batches : {}
  const recipes = raw !== null && typeof raw.recipes === 'object' ? raw.recipes : {}
  return { batches: batches ?? {}, recipes: recipes ?? {} }
}

function prune<T extends { at: number }>(record: Record<string, T>, max: number): void {
  const keys = Object.keys(record)
  if (keys.length <= max) return
  keys
    .sort((a, b) => (record[a]?.at ?? 0) - (record[b]?.at ?? 0))
    .slice(0, keys.length - max)
    .forEach((key) => {
      delete record[key]
    })
}

/** 그 배치의 기억을 읽는다. 없으면 빈 기억이다(예외를 던지지 않는다) */
export function readStepMemory(
  batch: Pick<Batch, 'batch_id' | 'recipe_id' | 'recipe_version' | 'sample_count'>,
): StepMemory {
  try {
    const stored = load()
    const batchCounts = new Map<ModuleKey, StepCount>()
    const modules = stored.batches[batch.batch_id]?.modules ?? {}
    for (const key of MODULE_KEYS) {
      const entry = modules[key]
      if (entry === undefined || entry === null) continue
      const step = isCount(entry.step) ? entry.step : undefined
      const total = isCount(entry.total) ? entry.total : undefined
      if (step === undefined && total === undefined) continue
      batchCounts.set(key, {
        ...(step === undefined ? {} : { step }),
        ...(total === undefined ? {} : { total }),
      })
    }
    const estimate = new Map<ModuleKey, number>()
    const recipeKey = recipeMemoryKey(batch)
    const totals = recipeKey === undefined ? {} : (stored.recipes[recipeKey]?.totals ?? {})
    for (const key of MODULE_KEYS) {
      const total = totals[key]
      if (isCount(total) && total > 0) estimate.set(key, total)
    }
    return { batch: batchCounts, estimate }
  } catch {
    return EMPTY_STEP_MEMORY
  }
}

/**
 * **실제로 들은 수**를 적는다. 🔴 기억(추정)을 다시 적지 않는다 — 부르는 쪽이 넘기는 것은 구독이 준 값뿐이다.
 * 같은 값이면 쓰지 않는다(1Hz 미러가 매번 저장소를 두드리지 않게).
 */
export function rememberSteps(
  batch: Pick<Batch, 'batch_id' | 'recipe_id' | 'recipe_version' | 'sample_count'>,
  heard: ModuleStepCounts,
  now: number = Date.now(),
): void {
  if (heard.size === 0) return
  try {
    const stored = load()
    let changed = false

    const prevBatch = stored.batches[batch.batch_id]?.modules ?? {}
    const nextBatch: Partial<Record<ModuleKey, StepCount>> = { ...prevBatch }
    for (const [key, count] of heard) {
      const prev = prevBatch[key]
      const step = isCount(count.step) ? count.step : prev?.step
      const total = isCount(count.total) ? count.total : prev?.total
      if (prev?.step === step && prev?.total === total) continue
      nextBatch[key] = {
        ...(step === undefined ? {} : { step }),
        ...(total === undefined ? {} : { total }),
      }
      changed = true
    }
    if (changed) {
      stored.batches[batch.batch_id] = { at: now, modules: nextBatch }
      prune(stored.batches, MAX_BATCHES)
    }

    const recipeKey = recipeMemoryKey(batch)
    if (recipeKey !== undefined) {
      const prevTotals = stored.recipes[recipeKey]?.totals ?? {}
      const nextTotals: Partial<Record<ModuleKey, number>> = { ...prevTotals }
      let recipeChanged = false
      for (const [key, count] of heard) {
        if (!isCount(count.total) || count.total <= 0) continue
        if (prevTotals[key] === count.total) continue
        nextTotals[key] = count.total
        recipeChanged = true
      }
      if (recipeChanged) {
        stored.recipes[recipeKey] = { at: now, totals: nextTotals }
        prune(stored.recipes, MAX_RECIPES)
        changed = true
      }
    }

    if (changed) safeStorage.writeJson(STORAGE_KEY, stored)
  } catch {
    /* 저장이 막혀도 화면은 선다 — 이번 세션은 구독이 준 값만으로 그린다 */
  }
}

/** 시험용 — 저장소 키를 밖에서 알 필요가 있을 때 */
export const STEP_MEMORY_STORAGE_KEY = STORAGE_KEY
